import React, { useEffect, useMemo, useRef, useState } from "react";
import { colorsLight } from "../../themes/colors";
import UnmetCheckboxIcon from "../images/UnmetCheckboxIcon";
import { makeStyles, useTheme } from "@material-ui/core";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import HelpIcon from "@material-ui/icons/Help";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExpandLess from "@material-ui/icons/ExpandLess";
import SingleSelectDropdown from "../Dropdown/SingleSelectDropdown";
import { Indication } from "@coherehealth/core-platform-api";

/* ────────── types ────────── */
export type Operator = "ALL" | "ANY" | "NOT_ANY" | "AT_LEAST" | "-";
export type LeafState = "met" | "unmet" | "unset";
export type HighlightMap = Record<string, string[]>;

/* ────────── variant resolution ────────── */
const COLLAPSIBLE_POLICY_TYPES = new Set(["INTERQUAL"]);

export function resolveGuidelineVariant(policyType?: string): "collapsible" | "standard" {
  return policyType && COLLAPSIBLE_POLICY_TYPES.has(policyType.toUpperCase()) ? "collapsible" : "standard";
}

/* ────────── styles ────────── */
const useStyles = makeStyles((theme) => ({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 12,
    background: "#ECEFF3",
    color: "#5B6572",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.25,
    marginRight: 6,
    verticalAlign: "top",
  },
  inlineControls: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 1,
    marginRight: 6,
    marginTop: 2,
    verticalAlign: "top",
  },
  pInline: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 0,
    margin: 0,
    padding: 0,
    lineHeight: 1.6,
  },
  dropdownWrap: {
    "& .MuiOutlinedInput-root": {
      height: theme.spacing(5),
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#DADFE8",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderWidth: 1,
    },
  },
  collapsibleCard: {
    borderRadius: 4,
    border: "1px solid #DADFE8",
    background: "#fff",
    boxShadow: "0px 3px 5px rgba(207, 226, 231, 0.3)",
    transition: "border-color 150ms ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  highlightChip: {
    background: "#FFDFA1",
    borderRadius: 4,
    padding: "4px 8px",
    marginBottom: 4,
    fontSize: 12,
    "&:hover": {
      background: "#FFDFA180",
    },
  },
}));

/* ────────── tiny icons (no innerHTML) ────────── */
const GroupPassIcon = CheckCircleIcon;
const GroupFailIcon = CancelIcon;
const GroupUndecidedIcon = HelpIcon;

const ThumbUpIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
  </svg>
);
const ThumbDownIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
  </svg>
);
const PencilIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);
const SaveEditIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);
const CancelEditIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
  </svg>
);

/* ────────── shared helpers (exported so container can reuse) ────────── */
export const toArr = <T,>(iter: ArrayLike<T> | Iterable<T> | null | undefined): T[] =>
  iter && (Symbol.iterator in Object(iter) || "length" in Object(iter)) ? Array.from(iter as any) : [];

// Minimal, allocation-safe decoder (no innerHTML, no DOM APIs)
export function decodeHtmlEntities(input: string): string {
  if (!input || !/[&]/.test(input)) {
    return input;
  }

  const named: Record<string, string> = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
    nbsp: " ",
  };

  return input.replace(/&(#x?[0-9A-Fa-f]+|[A-Za-z]+);/g, (_m, ent: string) => {
    if (named[ent]) {
      return named[ent];
    }

    if (ent[0] === "#") {
      const isHex = ent[1]?.toLowerCase() === "x";
      const numStr = ent.slice(isHex ? 2 : 1);
      const code = parseInt(numStr, isHex ? 16 : 10);
      if (!Number.isFinite(code)) {
        return `&${ent};`;
      }
      try {
        return String.fromCodePoint(code);
      } catch {
        return `&${ent};`;
      }
    }

    return `&${ent};`;
  });
}

export const hasEntity = (s: string) => /&lt;|&gt;|&amp;|&quot;|&#39;/.test(s);

export function sanitizeTree(root: Document | Element) {
  const forbidden = new Set([
    "SCRIPT",
    "STYLE",
    "LINK",
    "META",
    "IFRAME",
    "OBJECT",
    "EMBED",
    "FORM",
    "INPUT",
    "BUTTON",
  ]);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
  const removals: Element[] = [];
  let n = walker.currentNode as Element | null;
  while (n) {
    const tag = n.tagName?.toUpperCase?.() || "";
    if (forbidden.has(tag)) {
      removals.push(n);
    } else {
      // eslint-disable-next-line no-loop-func
      toArr(n.attributes).forEach((a) => {
        const name = a.name.toLowerCase();
        const val = (a.value || "").trim().toLowerCase();
        if (name.startsWith("on")) {
          n?.removeAttribute(a.name);
        }
        if (name === "href" || name === "src") {
          const safe =
            val.startsWith("http:") ||
            val.startsWith("https:") ||
            val.startsWith("mailto:") ||
            val.startsWith("tel:") ||
            (name === "src" && val.startsWith("data:image/"));
          if (!safe) {
            n?.removeAttribute(a.name);
          }
        }
      });
    }
    n = walker.nextNode() as Element | null;
  }
  removals.forEach((el) => el.remove());
}

export function connectorToOuterOp(c?: string): "ALL" | "ANY" | "NOT ANY" {
  const v = (c || "").trim().toUpperCase();
  if (v === "OR") {
    return "ANY";
  }
  if (v === "NOT") {
    return "NOT ANY";
  }
  return "ALL";
}
export function connectorLabel(c?: string): "AND" | "OR" | "AND NOT" {
  const v = (c || "").trim().toUpperCase();
  if (v === "OR") {
    return "OR";
  }
  if (v === "NOT") {
    return "AND NOT";
  }
  return "AND";
}

export function mergeReusables(
  mainHtml: string,
  reusableDocs: Array<{ id: string; title?: string; html: string }>
): string {
  if (!mainHtml) {
    return "";
  }
  const doc = new DOMParser().parseFromString(mainHtml, "text/html");
  sanitizeTree(doc);

  const placeholders = toArr(doc.querySelectorAll("reusable-content-node"));
  const usedIds = new Set<string>();

  placeholders.forEach((ph) => {
    const rid = ph.getAttribute("contentid") || "";
    const connRaw = ph.getAttribute("connectorlogic") || "AND";
    const opForEval = connectorToOuterOp(connRaw);
    const chip = connectorLabel(connRaw);

    const found = reusableDocs.find((d) => d.id === rid);
    const sub = new DOMParser().parseFromString(found?.html || "", "text/html");
    sanitizeTree(sub);

    const ul = doc.createElement("ul");
    ul.className = "vertical-line-list";
    ul.setAttribute("data-reusable", "true");

    const li = doc.createElement("li");
    li.setAttribute("data-logic-operator", opForEval);
    li.setAttribute("data-connector-label", chip);

    toArr(sub.body?.childNodes).forEach((node) => li.appendChild(doc.importNode(node, true)));

    ul.appendChild(li);
    ph.replaceWith(ul);
    if (found) {
      usedIds.add(found.id);
    }
  });

  // append any unplaced reusable blocks (default AND)
  const remaining = reusableDocs.filter((d) => !usedIds.has(d.id));
  remaining.forEach((d) => {
    const sub = new DOMParser().parseFromString(d.html || "", "text/html");
    sanitizeTree(sub);
    const ul = doc.createElement("ul");
    ul.className = "vertical-line-list";
    ul.setAttribute("data-reusable", "true");
    const li = doc.createElement("li");
    li.setAttribute("data-logic-operator", "ALL");
    li.setAttribute("data-connector-label", "AND");
    toArr(sub.body?.childNodes).forEach((node) => li.appendChild(doc.importNode(node, true)));
    ul.appendChild(li);
    doc.body.appendChild(ul);
  });

  return doc.body?.innerHTML || "";
}

/** Unwrap a span: move its children into its place, then remove the (now empty) span. */
function unwrapSpan(span: Element): void {
  const parent = span.parentNode;
  if (parent) {
    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span);
    }
    parent.removeChild(span);
  }
}

/**
 * Returns the finalized preview HTML, matching how content renders in the RTE
 * once comments and suggestions are resolved. OPEN items are shown as if
 * accepted so reviewers see what the policy WILL look like when finalized:
 * - Insertion (OPEN or ACCEPTED): unwrapped — text kept
 * - Insertion (REJECTED): removed — text dropped
 * - Deletion  (OPEN or ACCEPTED): removed — text dropped
 * - Deletion  (REJECTED): unwrapped — text kept
 * - Comments  (open or resolved): unwrapped — highlight dropped, text kept
 */
export function applyFinalizedSuggestionsAndComments(html: string): string {
  if (!html) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Deletions first, so removed subtrees don't interfere with later queries.
  // Accept (OPEN/ACCEPTED) a deletion => drop text; reject => keep text.
  toArr(doc.querySelectorAll('span[data-suggestion-type="deletion"]')).forEach((span) => {
    const status = span.getAttribute("data-suggestion-status") || "OPEN";
    if (status === "REJECTED") {
      unwrapSpan(span);
    } else {
      span.parentNode?.removeChild(span);
    }
  });

  // Accept (OPEN/ACCEPTED) an insertion => keep text; reject => drop text.
  toArr(doc.querySelectorAll('span[data-suggestion-type="insertion"]')).forEach((span) => {
    const status = span.getAttribute("data-suggestion-status") || "OPEN";
    if (status === "REJECTED") {
      span.parentNode?.removeChild(span);
    } else {
      unwrapSpan(span);
    }
  });

  // Comments (open or resolved) render without their highlight in the RTE — drop it, keep text.
  toArr(doc.querySelectorAll("span[data-comment-id]")).forEach((span) => {
    unwrapSpan(span);
  });

  return doc.body?.innerHTML || "";
}

/* ────────── internal helpers (not exported) ────────── */
function getOperatorForLi(el: Element): { op: Operator; atLeast?: number } {
  const raw = (el.getAttribute("data-logic-operator") || "").toUpperCase();
  const mapped: Operator | undefined =
    raw === "ALL"
      ? "ALL"
      : raw === "ANY"
      ? "ANY"
      : raw === "NOT ANY"
      ? "NOT_ANY"
      : raw === "AT LEAST"
      ? "AT_LEAST"
      : raw === "-"
      ? "-"
      : undefined;

  if (mapped === "AT_LEAST") {
    const atLeastVal = Number(el.getAttribute("data-at-least-value") || "1") || 1;
    return { op: "AT_LEAST", atLeast: atLeastVal };
  }
  if (mapped === "NOT_ANY") {
    return { op: "NOT_ANY" };
  }
  if (mapped) {
    return { op: mapped };
  }
  return { op: "-" };
}

function directChildLis(el: Element): Element[] {
  const ul = el.querySelector(":scope > ul");
  if (!ul) {
    return [];
  }
  return toArr(ul.children) as unknown as Element[];
}

function isEmptyLeaf(el: Element): boolean {
  const p = el.querySelector(":scope > p");
  const text = (p?.textContent || "").trim();
  if (text.length > 0) {
    return false;
  }
  const otherText = toArr(el.childNodes).some(
    (n) => n.nodeType === Node.TEXT_NODE && (n.textContent || "").trim().length > 0
  );
  return !otherText && directChildLis(el).length === 0;
}

/* ────────── Interactive renderer ────────── */
export type SectionOption = { id: string; label: string; html: string };

export function flattenIndicationUids(indications: Indication[]): string[] {
  const uids: string[] = [];
  function traverse(ind: Indication) {
    if (ind.uid) {
      uids.push(ind.uid);
    }
    (ind.indications || []).forEach(traverse);
  }
  indications.forEach(traverse);
  return uids;
}

export function InteractiveRichText({
  html,
  sections,
  onBannerChange = () => {},
  onLeafMapChange,
  onSectionChange,
  defaultSectionId,
  readOnly = false,
  initialLeafMap,
  initialHighlightMap,
  onHighlightChange,
  onHighlightClick,
  linkingText = null,
  onLinkConsumed,
  onIndicationLinked,
  indications,
  checkedIndications,
  unmetIndications,
  variant = "standard",
}: {
  html?: string;
  sections?: SectionOption[];
  onBannerChange?: (met: boolean) => void;
  onLeafMapChange?: (leafMap: Record<string, LeafState>) => void;
  onSectionChange?: (id: string) => void;
  defaultSectionId?: string;
  readOnly?: boolean;
  initialLeafMap?: Record<string, LeafState>;
  initialHighlightMap?: HighlightMap;
  onHighlightChange?: (highlightMap: HighlightMap) => void;
  onHighlightClick?: (indicationUid: string, text: string) => void;
  linkingText?: string | null;
  onLinkConsumed?: () => void;
  onIndicationLinked?: (indicationUid: string) => void;
  indications?: Indication[];
  checkedIndications?: Indication[];
  unmetIndications?: Indication[];
  variant?: "standard" | "collapsible";
}) {
  const classes = useStyles();
  const theme = useTheme();
  const [leafMap, setLeafMap] = useState<Record<string, LeafState>>(() => {
    const base = initialLeafMap ?? {};
    if (!checkedIndications?.length && !unmetIndications?.length) {
      return base;
    }
    const map: Record<string, LeafState> = { ...base };
    checkedIndications?.forEach((i) => {
      if (i.uid) {
        map[i.uid] = "met";
      }
    });
    unmetIndications?.forEach((i) => {
      if (i.uid) {
        map[i.uid] = "unmet";
      }
    });
    return map;
  });
  const [highlightMap, setHighlightMap] = useState<HighlightMap>(initialHighlightMap ?? {});

  // Sync external removals (e.g. PDF highlight deleted) into internal highlightMap.
  // We only apply removals — texts that disappeared from initialHighlightMap since the
  // last render — so we never undo chips the user added before context caught up.
  const prevInitialHighlightMapRef = useRef<HighlightMap>(initialHighlightMap ?? {});
  useEffect(() => {
    const prev = prevInitialHighlightMapRef.current;
    prevInitialHighlightMapRef.current = initialHighlightMap ?? {};
    const removedByUid: Record<string, string[]> = {};
    Object.keys(prev).forEach((uid) => {
      const prevTexts = prev[uid] ?? [];
      const nextTexts = (initialHighlightMap ?? {})[uid] ?? [];
      const removed = prevTexts.filter((t) => !nextTexts.includes(t));
      if (removed.length > 0) {
        removedByUid[uid] = removed;
      }
    });
    if (Object.keys(removedByUid).length === 0) {
      return;
    }
    setHighlightMap((current) => {
      let changed = false;
      const next = { ...current };
      Object.entries(removedByUid).forEach(([uid, removed]) => {
        if (!next[uid]) {
          return;
        }
        const filtered = next[uid].filter((t) => !removed.includes(t));
        if (filtered.length !== next[uid].length) {
          next[uid] = filtered;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [initialHighlightMap]);

  const [editingHighlight, setEditingHighlight] = useState<{ leafId: string; idx: number; value: string } | null>(null);
  const [pendingHighlight, setPendingHighlight] = useState<string | null>(null);
  const [hoveredLeafId, setHoveredLeafId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const allGroupIdsRef = useRef<string[]>([]);

  // external linkingText (from "Link to indication" button) takes precedence over internal selection
  const activePending = linkingText ?? pendingHighlight;
  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(defaultSectionId ?? sections?.[0]?.id);

  const activeHtml = sections
    ? sections.find((s) => s.id === selectedSectionId)?.html ?? sections[0]?.html ?? ""
    : html ?? "";

  // parse & sanitize once per html
  const doc = useMemo(() => {
    const d = new DOMParser().parseFromString(activeHtml || "", "text/html");
    sanitizeTree(d);
    return d;
  }, [activeHtml]);

  const rootNodes = useMemo(() => toArr(doc.body?.childNodes), [doc]);

  const idCounterRef = useRef(0);
  idCounterRef.current = 0;
  const indicationUids = useMemo(() => (indications ? flattenIndicationUids(indications) : []), [indications]);

  const computeState = (el: Element): "met" | "unmet" | "undecided" => {
    if (isEmptyLeaf(el)) {
      return "met";
    }

    const kids = directChildLis(el)
      .filter((k) => !isEmptyLeaf(k))
      .map(computeState);
    const { op, atLeast } = getOperatorForLi(el);

    if (kids.length === 0) {
      const id = el.getAttribute("data-react-id") || "";
      const state = (id && leafMap[id]) || "unset";
      if (state === "met") {
        return "met";
      }
      if (state === "unmet") {
        return "unmet";
      }
      return "undecided";
    }

    switch (op) {
      case "ALL":
        if (kids.some((s) => s === "unmet")) {
          return "unmet";
        }
        if (kids.every((s) => s === "met")) {
          return "met";
        }
        return "undecided";
      case "ANY":
        if (kids.some((s) => s === "met")) {
          return "met";
        }
        if (kids.every((s) => s === "unmet")) {
          return "unmet";
        }
        return "undecided";
      case "NOT_ANY":
        // A limitation only blocks coverage once it is affirmatively marked MET. Omitted or
        // not-met children leave the block satisfied, so an untouched NOT ANY block passes.
        if (kids.some((s) => s === "met")) {
          return "unmet";
        }
        return "met";
      case "AT_LEAST": {
        const need = Math.max(1, atLeast || 1);
        const countMet = kids.filter((s) => s === "met").length;
        const countPossible = kids.filter((s) => s !== "unmet").length;
        if (countMet >= need) {
          return "met";
        }
        if (countPossible < need) {
          return "unmet";
        }
        return "undecided";
      }
      default:
        if (kids.some((s) => s === "unmet")) {
          return "unmet";
        }
        if (kids.every((s) => s === "met")) {
          return "met";
        }
        return "undecided";
    }
  };

  useEffect(() => {
    const rootLis = toArr(
      doc.querySelectorAll(":scope > body > ul.vertical-line-list > li[data-logic-operator]")
    ) as Element[];
    if (rootLis.length === 0) {
      onBannerChange(false);
      return;
    }
    onBannerChange(rootLis.every((li) => computeState(li) === "met"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, leafMap, onBannerChange]);

  useEffect(() => {
    const lis = toArr(doc.querySelectorAll("li[data-logic-operator]")) as Element[];
    allGroupIdsRef.current = lis
      .filter((li) => (li.parentElement?.parentElement?.tagName || "").toLowerCase() !== "body")
      .map((li) => li.getAttribute("data-react-id") || "")
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, leafMap]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    const handler = () => {
      const text = window.getSelection()?.toString()?.trim() ?? "";
      if (text) {
        setPendingHighlight(text);
      }
    };
    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  }, [readOnly]);

  const renderNode = (node: Node, key: number): React.ReactNode => {
    if (node.nodeName === "BR") {
      return <br />;
    }
    if (node.nodeName === "COL") {
      const nodeElement = node as HTMLElement;
      const width = nodeElement.style.width;
      const style = width ? { width } : { minWidth: nodeElement.style.minWidth || "25px" };
      return <col style={style} />;
    }
    if (node?.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }
    const el = node as Element;

    // regular UL
    if (el.tagName === "UL" && !el.classList.contains("vertical-line-list")) {
      return (
        <ul key={key} style={{ marginTop: 0, marginBottom: 24, paddingLeft: 16, listStyleType: "disc" }}>
          {toArr(el.children).map((c, i) => renderNode(c as unknown as Node, i))}
        </ul>
      );
    }

    // logic UL
    if (el.tagName === "UL" && el.classList.contains("vertical-line-list")) {
      const isRoot = (el.parentElement?.tagName || "").toLowerCase() === "body";
      const isReusable = el.getAttribute("data-reusable") === "true";
      return (
        <div key={key} style={{ position: "relative", marginTop: isRoot && isReusable ? 24 : 0 }}>
          <div
            style={{ position: "absolute", left: theme.spacing(1), top: 0, bottom: 0, width: 4, background: "#F5F6F7" }}
          />
          <ul
            style={{
              margin: 0,
              marginBottom: theme.spacing(1),
              padding: 0,
              paddingLeft: theme.spacing(3),
              listStyle: "none",
            }}
          >
            {toArr(el.children).map((c, i) => renderNode(c as unknown as Node, i))}
          </ul>
        </div>
      );
    }

    // plain LI
    if (el.tagName === "LI" && !el.hasAttribute("data-logic-operator")) {
      return (
        <li key={key} style={{ margin: 0, marginBottom: 2 }}>
          {toArr(el.childNodes).map((c, i) => renderNode(c, i))}
        </li>
      );
    }

    // logic LI
    if (el.tagName === "LI" && el.hasAttribute("data-logic-operator")) {
      const kidLis = directChildLis(el).filter((k) => !isEmptyLeaf(k));
      const isLeaf = kidLis.length === 0;

      let rid = el.getAttribute("data-react-id");
      if (!rid) {
        const uidIdx = idCounterRef.current;
        ++idCounterRef.current;
        rid = (indicationUids.length > 0 && indicationUids[uidIdx]) || `li-${idCounterRef.current}`;
        el.setAttribute("data-react-id", rid);
      }

      const nodeState = computeState(el);
      const { op, atLeast } = getOperatorForLi(el);
      const opLabelFallback = op === "AT_LEAST" ? `AT LEAST ${atLeast ?? 1}` : op === "NOT_ANY" ? "NOT ANY" : op;
      const connectorChip = el.getAttribute("data-connector-label");
      const chipText = connectorChip || opLabelFallback;

      const firstP = el.querySelector(":scope > p");
      const firstTable = el.querySelector(":scope > table");
      const hasOwnLabel = !!(firstP && firstP.textContent && firstP.textContent.trim().length > 0);
      if (isLeaf && !hasOwnLabel && !firstTable) {
        return null;
      }

      if (!firstTable) {
        const metActive = (leafMap[rid!] || "unset") === "met";
        const unmetActive = (leafMap[rid!] || "unset") === "unmet";
        const leafHighlights = highlightMap[rid!] || [];

        const isLevelOne = (el.parentElement?.parentElement?.tagName || "").toLowerCase() === "body";
        const isExpanded = isLevelOne || expandedGroups.has(rid!);

        const circleIcon = connectorChip ? null : nodeState === "met" ? (
          <GroupPassIcon
            className="MuiSvgIcon-root"
            style={{ flexShrink: 0, width: 20, height: 20, color: colorsLight.success.main }}
          />
        ) : nodeState === "unmet" ? (
          <GroupFailIcon
            className="MuiSvgIcon-root"
            style={{ flexShrink: 0, width: 20, height: 20, color: colorsLight.error.main }}
          />
        ) : (
          <GroupUndecidedIcon
            className="MuiSvgIcon-root"
            style={{ flexShrink: 0, width: 20, height: 20, color: colorsLight.font.secondary }}
          />
        );

        const isChildList = (n: Node) =>
          n.nodeType === Node.ELEMENT_NODE &&
          (n as Element).tagName === "UL" &&
          (n as Element).classList.contains("vertical-line-list");
        const itemNodes = toArr(el.childNodes);
        const firstChildListIndex = itemNodes.findIndex(isChildList);
        const isAfterChildList = (index: number) => firstChildListIndex !== -1 && index > firstChildListIndex;

        const labelContent = (
          <span>
            {(() => {
              const out: React.ReactNode[] = [];
              const p = el.querySelector(":scope > p");
              if (p) {
                out.push(<span key="p">{toArr(p.childNodes).map((c, i) => renderNode(c, i))}</span>);
              }
              itemNodes.forEach((n, i) => {
                if (n !== p && !isChildList(n) && !isAfterChildList(i)) {
                  out.push(<span key={`other-${i}`}>{renderNode(n as Node, i)}</span>);
                }
              });
              return out;
            })()}
          </span>
        );

        // Content the item holds after its child list (notes, tables) keeps its place below the list
        const labelParagraph = el.querySelector(":scope > p");
        const trailingNodes = itemNodes.filter(
          (n, i) => n !== labelParagraph && !isChildList(n) && isAfterChildList(i)
        );
        const trailingContent =
          trailingNodes.length > 0 ? (
            <div data-testid="item-trailing-content">
              {trailingNodes.map((n, i) => (
                <span key={`trailing-${i}`}>{renderNode(n as Node, i)}</span>
              ))}
            </div>
          ) : null;

        const childrenULs = toArr(el.children)
          .filter((c) => (c as Element).tagName === "UL" && (c as Element).classList.contains("vertical-line-list"))
          .map((ul, i) => (
            <div key={`ul-${i}`} style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  left: theme.spacing(1),
                  top: 0,
                  bottom: 0,
                  width: 4,
                  background: "#F5F6F7",
                }}
              />
              <ul
                style={{
                  margin: 0,
                  marginBottom: theme.spacing(1),
                  padding: 0,
                  paddingLeft: theme.spacing(3),
                  listStyle: "none",
                }}
              >
                {toArr((ul as Element).children).map((li, j) => renderNode(li as unknown as Node, j))}
              </ul>
            </div>
          ));

        // card-only variant: connector line aligns under the circle icon (caret 18px + gap 4px + half-circle 10px = 32px from content edge)
        const childrenULsCard = toArr(el.children)
          .filter((c) => (c as Element).tagName === "UL" && (c as Element).classList.contains("vertical-line-list"))
          .map((ul, i) => (
            <div key={`ul-${i}`} style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  left: theme.spacing(3.625),
                  top: 0,
                  bottom: 0,
                  width: 4,
                  background: "#F5F6F7",
                }}
              />
              <ul
                style={{
                  margin: 0,
                  marginBottom: theme.spacing(1),
                  padding: 0,
                  paddingLeft: theme.spacing(6),
                  listStyle: "none",
                }}
              >
                {toArr((ul as Element).children).map((li, j) => renderNode(li as unknown as Node, j))}
              </ul>
            </div>
          ));

        // non-leaf groups
        if (!isLeaf) {
          // collapsible: level 1 always expanded (no card), level 2+ collapsible card
          if (variant === "collapsible") {
            if (isLevelOne) {
              return (
                <li key={key} style={{ margin: 0, marginBottom: 16, listStyle: "none" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: theme.spacing(1),
                      paddingBottom: theme.spacing(1),
                    }}
                  >
                    {circleIcon}
                    {connectorChip && <span className={classes.chip}>{chipText}</span>}
                    {labelContent}
                  </div>
                  {childrenULs}
                  {trailingContent}
                </li>
              );
            }
            return (
              <li key={key} style={{ margin: 0, marginBottom: 8, listStyle: "none" }}>
                <div className={classes.collapsibleCard}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(rid!)) {
                          next.delete(rid!);
                        } else {
                          next.add(rid!);
                        }
                        return next;
                      });
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: theme.spacing(0.5),
                      padding: theme.spacing(1),
                      cursor: "pointer",
                      background: "#fff",
                      border: "none",
                      textAlign: "left",
                      borderRadius: isExpanded ? "4px 4px 0 0" : 4,
                      fontFamily: "inherit",
                      fontSize: "inherit",
                    }}
                  >
                    {isExpanded ? (
                      <ExpandLess style={{ flexShrink: 0, width: 18, height: 18, color: colorsLight.font.secondary }} />
                    ) : (
                      <ExpandMore style={{ flexShrink: 0, width: 18, height: 18, color: colorsLight.font.secondary }} />
                    )}
                    {circleIcon}
                    {connectorChip && <span className={classes.chip}>{chipText}</span>}
                    <span style={{ flex: 1 }}>{labelContent}</span>
                  </button>
                  {isExpanded && (
                    <div style={{ padding: theme.spacing(1) }}>
                      {childrenULsCard}
                      {trailingContent}
                    </div>
                  )}
                </div>
              </li>
            );
          }

          // standard: flat rendering — binary badge (met = green, else red), chip always visible
          return (
            <li key={key} style={{ margin: 0, marginBottom: 2, listStyle: "none" }}>
              <p className={classes.pInline} style={connectorChip ? { alignItems: "baseline" } : undefined}>
                {connectorChip ? null : nodeState === "met" ? (
                  <GroupPassIcon
                    className="MuiSvgIcon-root"
                    style={{
                      display: "inline-flex",
                      width: 20,
                      height: 20,
                      color: colorsLight.success.main,
                      marginRight: 6,
                    }}
                  />
                ) : (
                  <GroupFailIcon
                    className="MuiSvgIcon-root"
                    style={{
                      display: "inline-flex",
                      width: 20,
                      height: 20,
                      color: colorsLight.error.main,
                      marginRight: 6,
                    }}
                  />
                )}
                <span className={classes.chip}>{chipText}</span>
                {labelContent}
              </p>
              {childrenULs}
              {trailingContent}
            </li>
          );
        }

        // leaf node
        return (
          <li
            key={key}
            data-react-id={rid!}
            onMouseEnter={!!activePending ? () => setHoveredLeafId(rid!) : undefined}
            onMouseLeave={!!activePending ? () => setHoveredLeafId(null) : undefined}
            onClick={
              !!activePending
                ? () => {
                    // If the click is part of a text-selection gesture (mouseup set
                    // pendingHighlight, then click fires on the same element), ignore it.
                    // The user must release the selection first, then explicitly click a leaf.
                    if ((window.getSelection()?.toString()?.trim() ?? "").length > 0) {
                      return;
                    }
                    const current = highlightMap[rid!] || [];
                    if (!current.includes(activePending!)) {
                      const updated = { ...highlightMap, [rid!]: [...current, activePending!] };
                      setHighlightMap(updated);
                      onHighlightChange?.(updated);
                      onIndicationLinked?.(rid!);
                    }
                    window.getSelection()?.removeAllRanges();
                    setPendingHighlight(null);
                    onLinkConsumed?.();
                    setHoveredLeafId(null);
                  }
                : undefined
            }
            style={{
              margin: 0,
              marginBottom: leafHighlights.length > 0 ? 8 : 2,
              borderRadius: activePending && hoveredLeafId === rid ? 4 : undefined,
              background: activePending && hoveredLeafId === rid ? "#EEF2FF" : "transparent",
              transition: !!activePending ? "background 0.1s" : undefined,
              cursor: !!activePending ? "pointer" : "default",
            }}
          >
            <p className={classes.pInline}>
              <span className={classes.inlineControls}>
                {readOnly ? (
                  <>
                    <CheckBoxIcon
                      className="MuiSvgIcon-root"
                      style={{
                        width: 20,
                        height: 20,
                        color: metActive ? theme.palette.primary.main : "#DADFE8",
                      }}
                    />
                    <UnmetCheckboxIcon
                      className="MuiSvgIcon-root"
                      style={{
                        width: 20,
                        height: 20,
                        color: unmetActive ? colorsLight.font.secondary : "#DADFE8",
                      }}
                    />
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      aria-label="mark met"
                      onClick={(e) => {
                        e.stopPropagation();
                        const cur = leafMap[rid!] || "unset";
                        const next: LeafState = cur === "met" ? "unset" : "met";
                        const updated = { ...leafMap, [rid!]: next };
                        setLeafMap(updated);
                        onLeafMapChange?.(updated);
                      }}
                      style={{ padding: 0, border: "none", background: "transparent", width: 20, height: 20 }}
                      title="mark met"
                    >
                      <CheckBoxIcon
                        className="MuiSvgIcon-root"
                        style={{
                          width: 20,
                          height: 20,
                          color: metActive ? theme.palette.primary.main : "#DADFE8",
                        }}
                      />
                    </button>
                    <button
                      type="button"
                      aria-label="mark unmet"
                      onClick={(e) => {
                        e.stopPropagation();
                        const cur = leafMap[rid!] || "unset";
                        const next: LeafState = cur === "unmet" ? "unset" : "unmet";
                        const updated = { ...leafMap, [rid!]: next };
                        setLeafMap(updated);
                        onLeafMapChange?.(updated);
                      }}
                      style={{ padding: 0, border: "none", background: "transparent", width: 20, height: 20 }}
                      title="mark unmet"
                    >
                      <UnmetCheckboxIcon
                        className="MuiSvgIcon-root"
                        style={{
                          width: 20,
                          height: 20,
                          color: unmetActive ? colorsLight.font.secondary : "#DADFE8",
                        }}
                      />
                    </button>
                  </>
                )}
              </span>
              {labelContent}
            </p>

            {leafHighlights.length > 0 && (
              <div style={{ marginLeft: 34, marginTop: 4 }}>
                {leafHighlights.map((text, idx) => {
                  const isEditing = editingHighlight?.leafId === rid! && editingHighlight?.idx === idx;
                  return (
                    <div key={idx} className={classes.highlightChip}>
                      {isEditing ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 4 }}>
                            <button
                              type="button"
                              title="Save"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newList = [...leafHighlights];
                                newList[idx] = editingHighlight!.value.trim() || text;
                                const updated = { ...highlightMap, [rid!]: newList };
                                setHighlightMap(updated);
                                onHighlightChange?.(updated);
                                setEditingHighlight(null);
                              }}
                              style={{
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                lineHeight: 1,
                              }}
                            >
                              <SaveEditIcon style={{ width: 14, height: 14, fill: "#DE7800" }} />
                            </button>
                            <button
                              type="button"
                              title="Cancel"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingHighlight(null);
                              }}
                              style={{
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                lineHeight: 1,
                              }}
                            >
                              <CancelEditIcon style={{ width: 14, height: 14, fill: "#DE780050" }} />
                            </button>
                          </div>
                          <textarea
                            autoFocus
                            value={editingHighlight!.value}
                            onChange={(e) => setEditingHighlight({ ...editingHighlight!, value: e.target.value })}
                            style={{
                              width: "100%",
                              fontSize: 12,
                              fontFamily: "inherit",
                              border: "1px solid #DE7800",
                              borderRadius: 4,
                              padding: "4px 6px",
                              background: "#fff",
                              resize: "vertical",
                              boxSizing: "border-box",
                              minHeight: 56,
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 4,
                            cursor: onHighlightClick ? "pointer" : "default",
                          }}
                          onClick={
                            onHighlightClick
                              ? (e) => {
                                  e.stopPropagation();
                                  onHighlightClick(rid!, text);
                                }
                              : undefined
                          }
                        >
                          <span style={{ flex: 1, color: colorsLight.font.main }}>
                            {text.length > 250 ? text.slice(0, 247) + "..." : text}
                          </span>
                          {!readOnly && (
                            <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                              <button
                                type="button"
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingHighlight({ leafId: rid!, idx, value: text });
                                }}
                                style={{
                                  padding: 0,
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  lineHeight: 1,
                                }}
                              >
                                <PencilIcon style={{ width: 12, height: 12, fill: "#DE780050" }} />
                              </button>
                              <ThumbUpIcon style={{ width: 12, height: 12, fill: "#DE7800" }} />
                              <button
                                type="button"
                                title="Remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newList = leafHighlights.filter((_, i) => i !== idx);
                                  const updated = { ...highlightMap };
                                  if (newList.length === 0) {
                                    delete updated[rid!];
                                  } else {
                                    updated[rid!] = newList;
                                  }
                                  setHighlightMap(updated);
                                  onHighlightChange?.(updated);
                                }}
                                style={{
                                  padding: 0,
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  lineHeight: 1,
                                }}
                              >
                                <ThumbDownIcon style={{ width: 12, height: 12, fill: "#DE780050" }} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </li>
        );
      }
    }

    // tables
    switch (el.tagName) {
      case "TABLE":
        return (
          <table key={key} style={{ borderCollapse: "collapse", width: "100%", margin: "16px 0" }}>
            {toArr(el.childNodes).map((c, i) => renderNode(c, i))}
          </table>
        );
      case "THEAD":
        return <thead key={key}>{toArr(el.childNodes).map((c, i) => renderNode(c, i))}</thead>;
      case "TBODY":
        return <tbody key={key}>{toArr(el.childNodes).map((c, i) => renderNode(c, i))}</tbody>;
      case "TR":
        return <tr key={key}>{toArr(el.childNodes).map((c, i) => renderNode(c, i))}</tr>;
      case "TD": {
        const colSpan = el.getAttribute("colspan");
        const rowSpan = el.getAttribute("rowspan");
        const tdInline: Record<string, string> = {};
        const tdStyleAttr = el.getAttribute("style") || "";
        tdStyleAttr.split(";").forEach((s: string) => {
          const [prop, val] = s.split(":").map((x: string) => x.trim());
          if (prop && val) {
            const camelProp = prop.replace(/-([a-z])/g, (g: string) => g[1].toUpperCase());
            tdInline[camelProp] = val;
          }
        });
        return (
          <td
            key={key}
            colSpan={colSpan ? parseInt(colSpan, 10) : undefined}
            rowSpan={rowSpan ? parseInt(rowSpan, 10) : undefined}
            style={{ ...tdInline, border: "1px solid #ccc", padding: "8px" }}
          >
            {toArr(el.childNodes).map((c, i) => renderNode(c, i))}
          </td>
        );
      }
      case "TH": {
        const colSpan = el.getAttribute("colspan");
        const rowSpan = el.getAttribute("rowspan");
        const thInline: Record<string, string> = {};
        const thStyleAttr = el.getAttribute("style") || "";
        thStyleAttr.split(";").forEach((s: string) => {
          const [prop, val] = s.split(":").map((x: string) => x.trim());
          if (prop && val) {
            const camelProp = prop.replace(/-([a-z])/g, (g: string) => g[1].toUpperCase());
            thInline[camelProp] = val;
          }
        });
        return (
          <th
            key={key}
            colSpan={colSpan ? parseInt(colSpan, 10) : undefined}
            rowSpan={rowSpan ? parseInt(rowSpan, 10) : undefined}
            style={{ ...thInline, border: "1px solid #ccc", padding: "8px", fontWeight: "bold" }}
          >
            {toArr(el.childNodes).map((c, i) => renderNode(c, i))}
          </th>
        );
      }
      case "COLGROUP":
      case "COL":
        return null;
      case "P": {
        const pStyle: Record<string, string> = { margin: "0", padding: "4px 0 8px" };
        const pStyleAttr = el.getAttribute("style") || "";
        pStyleAttr.split(";").forEach((s: string) => {
          const [prop, val] = s.split(":").map((x: string) => x.trim());
          if (prop && val) {
            const camelProp = prop.replace(/-([a-z])/g, (g: string) => g[1].toUpperCase());
            pStyle[camelProp] = val;
          }
        });
        return (
          <p key={key} style={pStyle}>
            {toArr(el.childNodes).map((c, i) => renderNode(c, i))}
          </p>
        );
      }
      default: {
        const tagName = el.tagName.toLowerCase();
        const props: any = { key };
        if (tagName === "a") {
          const href = el.getAttribute("href");
          const target = el.getAttribute("target");
          const rel = el.getAttribute("rel");
          if (href) {
            props.href = href;
          }
          if (target) {
            props.target = target;
          }
          if (rel) {
            props.rel = rel;
          } else if (target === "_blank") {
            props.rel = "noopener noreferrer";
          }
        }
        return React.createElement(
          tagName,
          props,
          toArr(el.childNodes).map((c, i) => renderNode(c, i))
        );
      }
    }
  };

  return (
    <div style={{ marginTop: theme.spacing(3), fontFamily: "Figtree" }}>
      {variant === "collapsible" && sections && sections.length >= 1 && (
        <div style={{ marginBottom: theme.spacing(1.5) }} className={classes.dropdownWrap}>
          <SingleSelectDropdown
            options={sections.map((s) => ({ id: s.id, label: s.label }))}
            value={selectedSectionId ?? sections[0]?.id ?? ""}
            onChange={(id) => {
              setSelectedSectionId(id);
              setLeafMap({});
              setHighlightMap({});
              setPendingHighlight(null);
              onSectionChange?.(id);
            }}
            variant="outlined"
          />
        </div>
      )}
      {variant === "collapsible" && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(1.5),
          }}
        >
          <button
            type="button"
            onClick={() => setExpandedGroups(new Set(allGroupIdsRef.current))}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "#039EC3",
              fontWeight: 600,
              fontSize: 14,
              fontFamily: "Figtree",
            }}
          >
            Expand all
          </button>
          <span style={{ color: "#9E9E9E", fontSize: 12, userSelect: "none" }}>|</span>
          <button
            type="button"
            onClick={() => setExpandedGroups(new Set())}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "#039EC3",
              fontWeight: 600,
              fontSize: 14,
              fontFamily: "Figtree",
            }}
          >
            Collapse all
          </button>
        </div>
      )}
      {rootNodes.map((n, i) => renderNode(n, i))}
    </div>
  );
}

export default InteractiveRichText;
